import type { AttestMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * Maps the tRPC mutation response to match the GraphQL mutation
 * response shape so consumers branching on the result do not need
 * to know which path executed.
 */
const mapTrpcResponseToGraphQL = (data: {
  affected_rows: number;
}): AttestMutation => ({
  attestRecord: {
    __typename: 'GenericMutationOutput',
    affected_rows: data.affected_rows,
  },
});

export const useAttestRecordTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.attestation.attest.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.attestation.status.queryKey(),
        });
      },
    }),
  });

  return {
    attest: async (input: { Id: string }): Promise<AttestMutation> => {
      const result = await mutation.mutateAsync({ Id: input.Id });

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
